import React, { useState, useRef, useEffect } from 'react';
import './ClientOnboarding.css';
import UI from '../config/uiLabels';
import SovereigntyConsent from './Legal/SovereigntyConsent';
import PRODUCT_DB_MAP, { PRODUCT_SLUG_TO_CANONICAL } from '../config/productDatabaseMap';
import { PRODUCT_OPTIONS, SECTOR_ORDER, PRODUCT_NAMES } from '../config/productCatalog';
import { API_BASE } from '../config/apiConfig';
import LocationSelector, { isLocationValid } from './LocationSelector';
import BatchOnboarding from './BatchOnboarding';

// Normalize product_type to PRODUCT_DB_MAP key and return demo DB config
// e.g. "Restaurant-Intelligence-Type-1" → "Restaurant.Type-1" → restaurant-ops-v1
const PNP_DEMO_CREDENTIALS = {
  engine: 'postgresql',
  host: 'localhost',
  port: '5432',
  username: 'pnp_readonly',
  password: 'PnP_R3ad0nly_2026!',
};

// All known demo host aliases — used for isUsingDemo detection
const DEMO_HOSTS = ['localhost', '127.0.0.1', 'pnp-demo-db'];

function getDemoDbForProductType(productType) {
  if (!productType) return { ...PNP_DEMO_CREDENTIALS, database: 'pnp-demo-db' };
  // Direct match
  if (PRODUCT_DB_MAP[productType]?.db) {
    return { ...PNP_DEMO_CREDENTIALS, database: PRODUCT_DB_MAP[productType].db };
  }
  // Try slug→canonical resolution (handles IndustrialWater-PFAS-Intelligence.Type-2 etc.)
  const canonical = PRODUCT_SLUG_TO_CANONICAL?.[productType];
  if (canonical && PRODUCT_DB_MAP[canonical]?.db) {
    return { ...PNP_DEMO_CREDENTIALS, database: PRODUCT_DB_MAP[canonical].db };
  }
  // Normalize: "Restaurant-Intelligence-Type-1" → "Restaurant.Type-1"
  const normalized = productType
    .replace(/-Intelligence-Type-(\d+)$/, '.Type-$1')
    .replace(/-Type-(\d+)$/, '.Type-$1');
  if (PRODUCT_DB_MAP[normalized]?.db) {
    return { ...PNP_DEMO_CREDENTIALS, database: PRODUCT_DB_MAP[normalized].db };
  }
  // Generic fallback — don't default to restaurant
  return { ...PNP_DEMO_CREDENTIALS, database: 'pnp-demo-db' };
}

const PLANS = {
  default: [
    { id: 'starter', name: 'Starter', price: '$1,000/mo', features: ['1 RAG PDF (100 pages)', '5 LLM use cases', '50 agreements/mo', '5 users'] },
    { id: 'growth', name: 'Growth', price: '$2,100/mo', features: ['5 RAG PDFs (500 pages)', '20 LLM use cases', '200 agreements/mo', '25 users'] },
    { id: 'enterprise', name: 'Enterprise', price: 'Custom', features: ['Unlimited PDFs', 'Unlimited use cases', 'Unlimited agreements', 'Unlimited users'] },
  ],
  retail_subscriber: [
    { id: 'starter', name: 'Starter', price: '$49/mo', features: ['Basic BI insights', '3 LLM queries/day', '1 RAG document', '1 user'], tag: 'Small shops' },
    { id: 'growth', name: 'Growth', price: '$49/mo', originalPrice: '$99/mo', discount: '50% off first year', features: ['Full BI + Top-10 questions', '20 LLM queries/day', '3 RAG documents', 'PDF export', '5 users'], tag: '⭐ Best for Restaurants / SMB', recommended: true },
    { id: 'enterprise', name: 'Premium', price: '$199/mo', features: ['Advanced analytics + custom queries', 'Unlimited LLM queries', '10 RAG documents', 'Shared BI + API access', '25 users'], tag: 'Multi-location' },
  ],
  geopolitical_subscription: [
    { id: 'executive_pulse', name: 'Executive Pulse', price: '$299/mo', trial_days: 14, features: ['Daily Pulse (all events)', 'Corporate Exposure (top 10)', 'Basic Sector Scores', '4-Pillar Classification', '30-day event history', '1 user'], tag: 'Individual executives', tooltip: 'EXECUTIVE PULSE: Get daily China-US event intelligence with sector scores and corporate impact rankings. Answers "What happened today?" with quantified severity scores. Includes: Daily Pulse dashboard, Top-10 corporate exposure, 11 GICS sector scores, Economy/Social/Cultural/Entertainment classification. Does NOT include personalized CID-DDLS or forecast scenarios.' },
    { id: 'portfolio_shield', name: 'Portfolio Shield', price: '$799/mo', trial_days: 14, features: ['Everything in Pulse', 'CID-DDLS Personalization', 'Portfolio Impact Alerts', 'Supply Chain Warnings', '90-day event history', '3 users'], tag: '⭐ Best for Portfolio Managers', recommended: true, tooltip: 'PORTFOLIO SHIELD: Every event filtered through YOUR company context, portfolio holdings, supply chain, and role. The CID-DDLS engine answers: "What does this mean for MY company? MY portfolio? MY supply chain? MY job?" Includes personalized Executive Signals, recommended actions, and CEO Directive alignment checking.' },
    { id: 'enterprise_command', name: 'Enterprise Command', price: '$2,499/mo', trial_days: 14, features: ['Everything in Shield', 'Forecast Scenarios (Bull/Base/Bear)', 'CEO Directives Upload', 'API Access + Webhooks', 'Historical Pattern Library', '5 team seats'], tag: 'C-Suite / Strategy Teams', tooltip: 'ENTERPRISE COMMAND: Full geopolitical decision intelligence with forecast scenarios (Bull 70% / Base 20% / Bear 10%), historical pattern matching (2018 trade war, 2020 tech ban), CEO Directives PDF upload for governance alignment, REST API + Slack/webhook delivery, and Nov 2026 tariff countdown analytics. Upload your China strategy PDF and every recommendation is checked against your directives.' },
    { id: 'institutional', name: 'Institutional', price: 'Custom', trial_days: 30, features: ['Everything in Command', 'White-label capability', 'Unlimited seats', 'Custom sector models', 'Dedicated analyst review', 'SLA guarantee'], tag: 'Banks / Hedge Funds', tooltip: 'INSTITUTIONAL: White-label the GeoPulse platform under your brand. Custom sector weighting models for your specific portfolio. Dedicated analyst review queue for critical events. SLA-guaranteed delivery times. Unlimited team seats. Shadow Calendar access (unstated commercial deadlines). Contact prab.balasubramanian@empirical-ais.com for pricing.' },
  ],
};

/**
 * ClientOnboarding — Reordered wizard flow:
 *   Step 1: Company Info + Plan
 *   Step 2: Database Setup (credentials + RLS config)
 *   Step 3: Schema Intelligence Extraction + LLM Consent
 *   Step 4: Upload PDFs (RAG, GenAI, Agentic)
 *   Step 5: Setup (trigger AI pipeline)
 *   Step 6: Done
 */
const ClientOnboarding = ({ onComplete, onLaunchApp, subscriberMode, editMode, forceNewBusiness }) => {
  const [step, setStep] = useState(editMode ? 1 : 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientId, setClientId] = useState(editMode?.client_id || null);
  const [uploadUrls, setUploadUrls] = useState(null);
  const [dashboardUrl, setDashboardUrl] = useState('');
  const [statusResult, setStatusResult] = useState(null);

  // Mall-specific state (Task 14.1)
  const [mallLocation, setMallLocation] = useState({
    street_address: '', city: '', state: '', zip_code: '',
  });
  const [mallLocationTouched, setMallLocationTouched] = useState(false);
  const [mallDashboardUrl, setMallDashboardUrl] = useState('');
  const [showBatchOnboarding, setShowBatchOnboarding] = useState(false);
  const [batchResult, setBatchResult] = useState(null);

  // Schema Intelligence state (Step 3)
  const [schemaCrawlResult, setSchemaCrawlResult] = useState(null);
  const [schemaConsent, setSchemaConsent] = useState(false);
  const [crawling, setCrawling] = useState(false);
  // Data Sovereignty dual-consent state
  const [sovereigntyConsent, setSovereigntyConsent] = useState({ consentA: false, consentB: false });
  const [distillationResult, setDistillationResult] = useState(null);
  // BI Sharing consent state (before GenAI config)
  const [biSharingConsent, setBiSharingConsent] = useState(false);
  const [biSharingClientIds, setBiSharingClientIds] = useState('');
  // GenAI Preview & Approval state (Step 4)
  const [genaiPreview, setGenaiPreview] = useState(null);
  const [genaiPreviewLoading, setGenaiPreviewLoading] = useState(false);
  const [genaiApproved, setGenaiApproved] = useState(false);

  // ── Payment / coupon state (subscriber mode only) ──────────────────────────
  // In subscriber mode, payment validation is the final step before onComplete.
  // The coupon input is shown inline in Step 7 so the user never sees a
  // separate payment modal — it's all part of the onboarding wizard.
  const [paymentValidated, setPaymentValidated] = useState(
    subscriberMode?.payment_validated || false
  );
  const [couponCode, setCouponCode] = useState('');
  const [couponMessage, setCouponMessage] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [paymentClientId, setPaymentClientId] = useState('');

  const handleApplyCouponInWizard = async (resolvedClientId) => {
    if (!couponCode.trim()) {
      setCouponMessage('Please enter a coupon code.');
      return;
    }
    setCouponApplying(true);
    setCouponMessage('');
    const API = process.env.REACT_APP_API_BASE || '/v1';
    try {
      const res = await fetch(`${API}/subscribers/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: resolvedClientId || clientId || '',
          signup_id: resolvedClientId || clientId || '',
          email: form.contact_email || subscriberMode?.email || '',
          product_type: form.product_type || subscriberMode?.product_type || '',
          product_name: form.product_name || subscriberMode?.product_name || '',
          company_name: form.company_name || subscriberMode?.company_name || '',
          coupon_code: couponCode.trim().toUpperCase(),
        }),
      });
      const data = await res.json();
      if (data.coupon_applied === true) {
        setPaymentValidated(true);
        setCouponMessage('✅ Coupon applied! Your free trial is activated.');
        setPaymentClientId(data.client_id || resolvedClientId || clientId || '');
      } else if (data.status === 'error') {
        setCouponMessage(`❌ ${data.message || 'Invalid coupon code.'}`);
      } else {
        setCouponMessage('❌ Coupon not accepted. Please check the code and try again.');
      }
    } catch {
      setCouponMessage('❌ Could not apply coupon. Please try again.');
    } finally {
      setCouponApplying(false);
    }
  };

  // Existing businesses modal state (duplicate email check)
  const [existingBusinesses, setExistingBusinesses] = useState(null);
  const [showExistingModal, setShowExistingModal] = useState(false);
  const [couponSuggestion, setCouponSuggestion] = useState('');

  // ── Product search/filter state (mirrors signup page) ──────────────────
  const [productSearch, setProductSearch] = useState('');
  const filteredProducts = PRODUCT_OPTIONS.filter(p => {
    if (!productSearch.trim()) return true;
    const q = productSearch.toLowerCase();
    return p.label.toLowerCase().includes(q) ||
           p.sector.toLowerCase().includes(q) ||
           p.subSector.toLowerCase().includes(q) ||
           (p.emoji && p.emoji.includes(q));
  });

  const [form, setForm] = useState({
    company_name: editMode?.company_name || '',
    ein: editMode?.ein || '',
    location: editMode?.location || '',
    contact_email: editMode?.contact_email || subscriberMode?.email || subscriberMode?.contact_email || '',
    website_url: editMode?.website_url || '',
    contact_phone: editMode?.contact_phone || '',
    product_name: editMode?.product_name || subscriberMode?.product_name || '',
    product_type: editMode?.product_type || subscriberMode?.product_type || '',
    plan: editMode?.plan || subscriberMode?.plan || 'starter',
    tenant_type: editMode?.tenant_type || subscriberMode?.tenant_type || 'standalone',
    tenant_role: editMode?.tenant_role || subscriberMode?.tenant_role || 'subscriber',
    parent_tenant_id: editMode?.parent_tenant_id || '',
    franchise_or_retail_subscription: editMode?.franchise_or_retail_subscription || 'retail_subscription',
  });

  // Apply subscriber mode defaults on mount + pre-populate business_address from session
  useEffect(() => {
    // Read business_address from localStorage session (Requirement 1.2)
    let sessionAddress = '';
    try {
      const session = JSON.parse(localStorage.getItem('pnp_subscriber_session') || '{}');
      if (session.business_address) {
        sessionAddress = session.business_address;
      }
    } catch { /* ignore parse errors */ }

    if (subscriberMode?.locked) {
      setForm(prev => ({
        ...prev,
        company_name: subscriberMode.company_name || prev.company_name,
        contact_email: subscriberMode.email || prev.contact_email,
        product_type: subscriberMode.product_type || prev.product_type,
        product_name: subscriberMode.product_name || prev.product_name,
        plan: 'growth',
        tenant_role: 'subscriber',
        tenant_type: 'standalone',
        // In edit mode, preserve the backend-loaded location (editMode.location)
        // Only fall back to session address for fresh onboards without an existing location.
        location: editMode?.location || sessionAddress || prev.location,
      }));
    } else if (sessionAddress && !editMode) {
      // Even outside subscriber mode, pre-populate address if session has it
      setForm(prev => ({
        ...prev,
        location: sessionAddress || prev.location,
      }));
    }
  }, []); // eslint-disable-line

  const [files, setFiles] = useState({ rag_ai: null, genai: null, agentic_ai: null });
  const [uploadProgress, setUploadProgress] = useState({ rag_ai: null, genai: null, agentic_ai: null });

  const [dbConfig, setDbConfig] = useState(() => {
    if (editMode?.db_config) {
      const storedDb = editMode.db_config.database || '';
      const storedHost = editMode.db_config.host || '';
      const isStaleDemo = DEMO_HOSTS.includes(storedHost) && (
        storedDb === 'restaurant-ops-v1' || storedDb === 'pnp-demo-db' || storedDb === ''
      );
      // If stored db is a stale/generic demo, upgrade to the correct product-type demo
      if (isStaleDemo && (editMode?.product_type || subscriberMode?.product_type)) {
        const correctDemo = getDemoDbForProductType(editMode?.product_type || subscriberMode?.product_type);
        if (correctDemo.database && correctDemo.database !== storedDb) {
          return {
            engine: editMode.db_config.engine || 'postgresql',
            host: correctDemo.host,
            port: correctDemo.port,
            database: correctDemo.database,
            username: correctDemo.username,
            password: '',
            is_sub_client: false,
            parent_chain_id: '',
            allow_distributor_insights: false,
            distributor_agent_ids: '',
          };
        }
      }
      return {
        engine: editMode.db_config.engine || 'postgresql',
        host: editMode.db_config.host || '',
        port: String(editMode.db_config.port || '5432'),
        database: editMode.db_config.database || '',
        username: editMode.db_config.username || '',
        password: '',  // Never pre-fill password for security
        is_sub_client: false,
        parent_chain_id: '',
        allow_distributor_insights: false,
        distributor_agent_ids: '',
      };
    }
    if (subscriberMode?.locked) {
      const demoDb = getDemoDbForProductType(subscriberMode.product_type);
      return {
        engine: 'postgresql',
        host: demoDb.host,
        port: demoDb.port,
        database: demoDb.database,
        username: demoDb.username,
        password: demoDb.password,
        is_sub_client: false,
        parent_chain_id: '',
        allow_distributor_insights: false,
        distributor_agent_ids: '',
      };
    }
    return {
      engine: 'postgresql',
      host: '',
      port: '5432',
      database: '',
      username: '',
      password: '',
      is_sub_client: false,
      parent_chain_id: '',
      allow_distributor_insights: false,
      distributor_agent_ids: '',
    };
  });

  // Supported database engines (fetched from API)
  const [supportedEngines, setSupportedEngines] = useState([]);

  // DB connection test state
  const [dbTestStatus, setDbTestStatus] = useState(null); // null | 'testing' | 'ok' | 'error'
  const [dbTestMessage, setDbTestMessage] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/supported-engines`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && data.engines) {
          setSupportedEngines(data.engines);
        }
      })
      .catch(() => {
        // Fallback: use hardcoded defaults if API is unavailable
        setSupportedEngines([
          { engine: 'postgresql', display_name: 'PostgreSQL', default_port: 5432 },
          { engine: 'mysql', display_name: 'MySQL', default_port: 3306 },
          { engine: 'mssql', display_name: 'SQL Server', default_port: 1433 },
          { engine: 'sqlite', display_name: 'SQLite', default_port: 0 },
          { engine: 'mariadb', display_name: 'MariaDB', default_port: 3306 },
        ]);
      });
  }, []);

  const ragRef = useRef();
  const genaiRef = useRef();
  const agenticRef = useRef();

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDbChange = (e) => {
    const { name, value } = e.target;
    setDbModified(true); // Mark as modified so edit mode re-tests
    // Reset connection test status whenever any DB field changes
    if (dbTestStatus !== null) {
      setDbTestStatus(null);
      setDbTestMessage('');
    }
    if (name === 'engine') {
      // Auto-update port to the engine's default port
      const engineInfo = supportedEngines.find(eng => eng.engine === value);
      const defaultPort = engineInfo ? String(engineInfo.default_port) : '5432';
      setDbConfig({ ...dbConfig, engine: value, port: defaultPort });
    } else {
      setDbConfig({ ...dbConfig, [name]: value });
    }
  };

  const handleFileSelect = (key, e) => {
    const file = e.target.files[0];
    if (file) {
      setFiles({ ...files, [key]: file });
    }
  };

  // Step 1: Register client (or update in edit mode)
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // ── Edit mode: update existing client ──
      if (editMode && clientId) {
        const res = await fetch(`${API_BASE}/clients/${clientId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        if (res.ok) {
          setStep(2);
        } else {
          // Even if PUT fails (endpoint may not exist), allow navigation
          setStep(2);
        }
        setLoading(false);
        return;
      }

      // ── Mall tenant type — route to /v1/mall/register (Task 14.1) ──────
      if (form.tenant_type === 'mall') {
        // Validate location before submitting
        setMallLocationTouched(true);
        if (!isLocationValid(mallLocation)) {
          setError('Please fill in all mall location fields (street address, city, state, ZIP code).');
          setLoading(false);
          return;
        }
        const mallRes = await fetch(`${API_BASE}/mall/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company_name: form.company_name,
            contact_email: form.contact_email,
            website_url: form.website_url || '',
            contact_phone: form.contact_phone || '',
            mall_location: mallLocation,
            provider: {
              name: 'Empirical-AIs',
              address: '9622 Ardrey Woods Dr, Charlotte, NC-28277',
              contact_email: form.contact_email,
            },
          }),
        });
        if (!mallRes.ok) {
          const errData = await mallRes.json().catch(() => ({}));
          throw new Error(errData.error || `Mall registration failed (${mallRes.status})`);
        }
        const mallData = await mallRes.json();
        setClientId(mallData.client_id);
        setUploadUrls(mallData.upload_urls);
        setDashboardUrl(mallData.dashboard_url);
        setMallDashboardUrl(mallData.dashboard_url);
        setStep(2);
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_BASE}/clients/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`Registration failed (${res.status})`);
      const data = await res.json();

      // ── Duplicate email detected — show existing businesses modal ──────
      // When forceNewBusiness is true (user came from restricted/payment-gate "Onboard Client"),
      // skip showing existing businesses — force "Create New Business" path only.
      // This prevents users from selecting a fully-provisioned business to bypass payment.
      // When forceNewBusiness is false (normal signup flow), show the picker with all options.
      if (data.status === 'existing_email' && data.businesses?.length > 0) {
        if (forceNewBusiness) {
          // Skip modal — re-submit with skip_duplicate_check to force new business
          const retryRes = await fetch(`${API_BASE}/clients/onboard`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...form, skip_duplicate_check: true }),
          });
          if (!retryRes.ok) throw new Error(`Registration failed (${retryRes.status})`);
          const retryData = await retryRes.json();
          setClientId(retryData.client_id);
          setUploadUrls(retryData.upload_urls);
          setDashboardUrl(retryData.dashboard_url);
          setStep(2);
          setLoading(false);
          return;
        }
        setExistingBusinesses(data.businesses);
        setCouponSuggestion(data.coupon_suggestion || '');
        setShowExistingModal(true);
        setLoading(false);
        return;
      }

      setClientId(data.client_id);
      setUploadUrls(data.upload_urls);
      setDashboardUrl(data.dashboard_url);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle "Create New Business" from existing businesses modal
  const handleCreateNewBusiness = async () => {
    setShowExistingModal(false);
    setExistingBusinesses(null);
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/clients/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, skip_duplicate_check: true }),
      });
      if (!res.ok) throw new Error(`Registration failed (${res.status})`);
      const data = await res.json();
      setClientId(data.client_id);
      setUploadUrls(data.upload_urls);
      setDashboardUrl(data.dashboard_url);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle selecting an existing business from the modal
  const handleSelectExistingBusiness = (business) => {
    setShowExistingModal(false);
    setExistingBusinesses(null);

    // Build a full config from the business data so the portal loads correctly
    const fullConfig = {
      client_id: business.client_id,
      company_name: business.company_name || form.company_name,
      product_name: business.product_name || business.product_type || form.product_name,
      product_type: business.product_type || form.product_type,
      plan: business.plan || form.plan,
      status: business.status || 'active',
      tenant_type: 'INDEPENDENT',
      tenant_role: 'subscriber',
    };

    // If in subscriber mode, update the subscriber session so the portal picks it up
    if (subscriberMode?.locked) {
      try {
        const existingSession = JSON.parse(localStorage.getItem('pnp_subscriber_session') || '{}');
        const updatedSession = {
          ...existingSession,
          client_id: business.client_id,
          company_name: fullConfig.company_name,
          product_type: fullConfig.product_type,
          product_name: fullConfig.product_name,
          plan: fullConfig.plan,
          onboarded: true,
          payment_validated: true,
        };
        localStorage.setItem('pnp_subscriber_session', JSON.stringify(updatedSession));
        // Reload to pick up the updated session
        window.location.href = '/subscriber';
        return;
      } catch { /* fall through to onLaunchApp */ }
    }

    // Navigate to the existing client's dashboard
    if (onLaunchApp) {
      onLaunchApp(fullConfig);
    }
  };

  // Track if DB config was modified in edit mode
  const [dbModified, setDbModified] = useState(false);

  // Step 2: Configure database (NOW step 2 instead of step 3)
  const handleDbSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // ── Edit mode: if DB config hasn't changed, skip test and proceed ──────
    if (editMode && !dbModified) {
      setStep(3);
      return;
    }

    // ── Step 2a: Test DB connection before saving ──────────────────────────
    setDbTestStatus('testing');
    setDbTestMessage('');
    try {
      const testRes = await fetch(`${API_BASE}/clients/test-db-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...dbConfig, port: parseInt(dbConfig.port, 10) }),
      });
      const testData = await testRes.json();
      if (testData.status !== 'ok') {
        setDbTestStatus('error');
        setDbTestMessage(testData.message || 'Connection failed. Please check your credentials.');
        return; // Block progression — stay on Step 2
      }
      setDbTestStatus('ok');
      setDbTestMessage(testData.message || 'Connected successfully');
    } catch (err) {
      setDbTestStatus('error');
      setDbTestMessage('Could not reach the server to test the connection. Please try again.');
      return;
    }

    // ── Step 2b: Save credentials + crawl schema ───────────────────────────
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/database`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...dbConfig, port: parseInt(dbConfig.port, 10) }),
      });
      if (!res.ok) throw new Error(`Database config failed (${res.status})`);
      // After DB is saved, trigger schema crawl automatically
      await handleSchemaCrawl();
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const skipDb = () => {
    setSchemaCrawlResult(null);
    setStep(5); // Skip DB + schema + preview → go straight to PDF upload
  };

  // Step 3: Schema Intelligence Extraction
  const handleSchemaCrawl = async () => {
    setCrawling(true);
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/schema-crawl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setSchemaCrawlResult(data);
      } else {
        // Schema crawl is best-effort
        setSchemaCrawlResult({ status: 'error', message: 'Schema crawl unavailable', tables: [], summary: { table_count: 0, total_columns: 0, object_count: 0 } });
      }
    } catch {
      setSchemaCrawlResult({ status: 'error', message: 'Could not connect to database for schema extraction', tables: [], summary: { table_count: 0, total_columns: 0, object_count: 0 } });
    } finally {
      setCrawling(false);
    }
  };

  // Step 3 → Sovereignty consent granted: distill + save + proceed
  const handleSovereigntyGranted = async ({ consentA, consentB }) => {
    setSovereigntyConsent({ consentA, consentB });
    setSchemaConsent(true);
    setLoading(true);
    setError('');
    try {
      // Trigger local distillation (Teacher → Student, air-gapped)
      const distillRes = await fetch(`${API_BASE}/clients/${clientId}/distill-local`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consent_a: consentA,
          consent_b: consentB,
        }),
      });
      if (distillRes.ok) {
        const distillData = await distillRes.json();
        setDistillationResult(distillData);
      }

      // Save consent + schema metadata to backend
      await fetch(`${API_BASE}/clients/${clientId}/schema-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consent: true,
          consent_a: consentA,
          consent_b: consentB,
          sovereignty_type: 'dual_consent',
          schema_metadata: schemaCrawlResult,
        }),
      });
    } catch { /* consent save is best-effort */ }
    setLoading(false);
    setStep(4);
    // Auto-load GenAI preview after sovereignty consent
    loadGenaiPreview();
  };

  // Step 3 → Sovereignty consent denied: skip schema intelligence
  const handleSovereigntyDenied = async () => {
    setSovereigntyConsent({ consentA: false, consentB: false });
    setSchemaConsent(false);
    try {
      await fetch(`${API_BASE}/clients/${clientId}/schema-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consent: false, consent_a: false, consent_b: false }),
      });
    } catch { /* best-effort */ }
    setStep(4);
    loadGenaiPreview();
  };

  // Step 4: Load GenAI Preview for pnp_admin review
  const loadGenaiPreview = async () => {
    setGenaiPreviewLoading(true);
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/genai-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setGenaiPreview(data);
      } else {
        // Fallback: build a minimal preview from what we have
        setGenaiPreview({
          status: 'assembled',
          config_markdown: `# ${form.company_name} — GenAI Configuration\n\n**Product Type:** ${form.product_type}\n**Status:** PENDING APPROVAL\n\n_Backend preview unavailable — approve to proceed with template configuration._`,
          sections: [],
          metadata: { company_name: form.company_name, product_type: form.product_type, table_count: schemaCrawlResult?.summary?.table_count || 0 },
        });
      }
    } catch {
      setGenaiPreview({
        status: 'assembled',
        config_markdown: `# ${form.company_name} — GenAI Configuration\n\n**Product Type:** ${form.product_type}\n**Status:** PENDING APPROVAL\n\n_Preview generated locally._`,
        sections: [],
        metadata: { company_name: form.company_name, product_type: form.product_type, table_count: 0 },
      });
    }
    setGenaiPreviewLoading(false);
  };

  // Step 4: Approve GenAI config
  const handleGenaiApprove = async () => {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/clients/${clientId}/genai-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config_markdown: genaiPreview?.config_markdown || '' }),
      });
    } catch { /* best-effort */ }
    setGenaiApproved(true);
    setLoading(false);
    setStep(5);
  };

  // Step 4: Upload PDFs via presigned URLs (was step 2)
  const uploadFile = async (key) => {
    const file = files[key];
    const url = uploadUrls[key];
    if (!file || !url) return;
    setUploadProgress((prev) => ({ ...prev, [key]: 'uploading' }));
    try {
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/pdf' },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload failed for ${key}`);
      setUploadProgress((prev) => ({ ...prev, [key]: 'done' }));
    } catch {
      setUploadProgress((prev) => ({ ...prev, [key]: 'error' }));
    }
  };

  const handleUploadAll = async () => {
    setLoading(true);
    setError('');
    await Promise.all(['rag_ai', 'genai', 'agentic_ai'].map(uploadFile));
    setLoading(false);
    // In subscriber mode: go to payment/coupon step before triggering setup
    // In admin mode: go directly to setup
    if (subscriberMode && !paymentValidated) {
      setStep(6); // payment step
    } else {
      setStep(subscriberMode ? 7 : 6); // setup step
    }
  };

  // Step 6: Trigger setup, provision tenant security views, & poll status
  const handleTriggerSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/setup`, { method: 'POST' });
      if (!res.ok) throw new Error(`Setup trigger failed (${res.status})`);

      const tenantTypeMap = { standalone: 'INDEPENDENT', parent: 'PARENT', sub_client: 'SUB_CLIENT' };
      try {
        await fetch(`${API_BASE}/clients/${clientId}/provision-tenant`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant_id: clientId,
            name: form.company_name,
            type: tenantTypeMap[form.tenant_type] || 'INDEPENDENT',
            parent_id: form.parent_tenant_id || null,
          }),
        });
      } catch { /* provision is best-effort */ }

      setTimeout(async () => {
        try {
          const statusRes = await fetch(`${API_BASE}/clients/${clientId}/status`);
          if (statusRes.ok) {
            setStatusResult(await statusRes.json());
          }
        } catch { /* status check is best-effort */ }
        setStep(7);
        setLoading(false);
        // onComplete is now called from the "Next →" button at Step 7,
        // which ensures all 8 stages complete before exiting the wizard.
      }, 3000);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const stepLabels = subscriberMode
    ? ['Company Info', 'Database', 'Schema Intelligence', 'GenAI Preview', 'Upload PDFs', 'Activate Account', 'Setup', 'Done']
    : ['Company Info', 'Database', 'Schema Intelligence', 'GenAI Preview', 'Upload PDFs', 'Setup', 'Review', 'Done'];

  return (
    <div className="client-onboarding">
      {/* ── Existing Businesses Modal ──────────────────────────────────────── */}
      {showExistingModal && existingBusinesses && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '32px',
            maxWidth: '600px', width: '90%', maxHeight: '80vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <h2 style={{ margin: '0 0 8px', color: '#1a1a2e' }}>📋 Email Already Registered</h2>
            <p style={{ color: '#666', margin: '0 0 20px', fontSize: '14px' }}>
              <strong>{form.contact_email}</strong> already has {existingBusinesses.length} registered business{existingBusinesses.length > 1 ? 'es' : ''}.
              Select one to continue, or create a new business.
            </p>

            {/* Coupon suggestion for returning users with new product type */}
            {couponSuggestion && (
              <div style={{ background: '#FFF3E0', border: '1px solid #FFB74D', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#E65100' }}>
                <strong>🎟️ Coupon Available:</strong> {couponSuggestion}
              </div>
            )}

            {existingBusinesses.map((biz, idx) => (
              <div key={biz.client_id || idx} onClick={() => handleSelectExistingBusiness(biz)} style={{
                border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px',
                marginBottom: '12px', cursor: 'pointer', transition: 'all 0.2s',
                background: '#f9f9f9',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.background = '#f0f0ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = '#f9f9f9'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong style={{ fontSize: '16px', color: '#1a1a2e' }}>{biz.company_name || 'Unnamed Business'}</strong>
                    <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                      {biz.product_name || biz.product_type || 'No product'} &nbsp;·&nbsp; {biz.plan || 'No plan'} &nbsp;·&nbsp;
                      <span style={{ color: biz.status === 'active' ? '#16a34a' : '#f59e0b', fontWeight: 600 }}>
                        {biz.status || 'unknown'}
                      </span>
                    </div>
                  </div>
                  <span style={{ color: '#4f46e5', fontWeight: 600, fontSize: '14px' }}>Open →</span>
                </div>
              </div>
            ))}

            <div style={{ borderTop: '1px solid #eee', paddingTop: '16px', marginTop: '8px', display: 'flex', gap: '12px' }}>
              <button onClick={handleCreateNewBusiness} style={{
                flex: 1, padding: '12px', borderRadius: '8px', border: '2px solid #4f46e5',
                background: '#fff', color: '#4f46e5', fontWeight: 600, cursor: 'pointer', fontSize: '14px',
              }}>
                + Create New Business
              </button>
              <button onClick={() => { setShowExistingModal(false); setExistingBusinesses(null); }} style={{
                padding: '12px 20px', borderRadius: '8px', border: '1px solid #ddd',
                background: '#f5f5f5', color: '#666', cursor: 'pointer', fontSize: '14px',
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="onboarding-header" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <img 
          src="/PNP_RAG_GENAI_Agentic_ai.jpeg" 
          alt="PNP RAG GenAI Agentic AI" 
          style={{ width: '80px', height: '80px', borderRadius: '12px', objectFit: 'cover', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
        />
        <div>
          <h1>{editMode ? '✏️ Update Client' : '🚀 Client Onboarding'}</h1>
          <p>{editMode ? `Update settings for ${editMode.company_name || clientId}` : 'Register a new subscriber to Plug-N-Play-Infra.ai'}</p>
        </div>
      </div>

      {/* Progress bar — clickable in edit mode */}
      <div className="step-progress">
        {stepLabels.map((label, i) => (
          <div
            key={label}
            className={`step-dot ${step > i ? 'completed' : ''} ${step === i + 1 ? 'active' : ''}`}
            onClick={() => { if (editMode && clientId) setStep(i + 1); }}
            style={editMode && clientId ? { cursor: 'pointer' } : {}}
          >
            <span className="dot">{step > i + 1 ? '✓' : i + 1}</span>
            <span className="step-label">{label}</span>
          </div>
        ))}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Step 1: Company Info + Plan */}
      {step === 1 && (
        <form className="card" onSubmit={handleRegister}>
          <h2>Company Information</h2>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="company_name">Company Name *</label>
              <input id="company_name" name="company_name" value={form.company_name} onChange={handleFormChange} placeholder="Prab-Restaurant-Hospitality" required disabled={subscriberMode?.locked} style={subscriberMode?.locked ? { background: '#f5f5f5', color: '#666' } : {}} />
            </div>
            <div className="field">
              <label htmlFor="ein">EIN (Tax ID) <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span></label>
              <input id="ein" name="ein" value={form.ein} onChange={handleFormChange} placeholder="XX-XXXXXXX" />
            </div>
            <div className="field">
              <label htmlFor="location">Location *</label>
              <input id="location" name="location" value={form.location} onChange={handleFormChange} placeholder="Charlotte, NC 28277" required autoComplete="off" />
              <div style={{
                marginTop: '6px', padding: '7px 10px', background: '#E8F5E9',
                border: '1px solid #A5D6A7', borderRadius: '6px',
                fontSize: '11px', color: '#2E7D32', lineHeight: 1.5,
              }}>
                📍 <strong>Guardrail Notice:</strong> This address configures your AI Guardrail boundary — the system scans your catalog products and services within this geographic scope to protect your business from out-of-area or non-compliant queries.
              </div>
            </div>
            <div className="field">
              <label htmlFor="contact_email">Contact Email *</label>
              <input id="contact_email" name="contact_email" type="email" value={form.contact_email} onChange={handleFormChange} placeholder="admin@company.com" required disabled={subscriberMode?.locked} style={subscriberMode?.locked ? { background: '#f5f5f5', color: '#666' } : {}} />
              {/* Email + alias warning — shown when user types a + before @ */}
              {form.contact_email && /\+[^@]+@/.test(form.contact_email) && !form.contact_email.toLowerCase().endsWith('@empirical-ais.com') && (
                <div style={{
                  marginTop: '6px', padding: '7px 10px',
                  background: '#FFF8E1', border: '1px solid #FFD54F',
                  borderRadius: '6px', fontSize: '11px', color: '#E65100', lineHeight: 1.5,
                }}>
                  ⚠️ <strong>Email alias detected (+tag).</strong> Your account will be linked to the base email address <code style={{ background: '#FFE082', padding: '1px 4px', borderRadius: '3px' }}>{form.contact_email.replace(/\+[^@]+@/, '@')}</code> for subscription tracking. Using email aliases to restart free trials is a violation of our Terms of Service.
                </div>
              )}
            </div>
            <div className="field">
              <label htmlFor="website_url">Website URL</label>
              <input id="website_url" name="website_url" type="url" value={form.website_url} onChange={handleFormChange} placeholder="https://www.company.com" />
            </div>
            <div className="field">
              <label htmlFor="contact_phone">Contact Phone</label>
              <input id="contact_phone" name="contact_phone" type="tel" value={form.contact_phone} onChange={handleFormChange} placeholder="704-555-0100" />
            </div>
            <div className="field full-width">
              <label htmlFor="product_type" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Product Type *</span>
                <span
                  title={`📊 Catalog of Products and Services\n\nTotal Products: ${PRODUCT_OPTIONS.length}\nTotal Sectors: ${SECTOR_ORDER.length}\nLive Databases: ${PRODUCT_OPTIONS.filter(p => p.dbReady).length}\n\nSectors: ${SECTOR_ORDER.join(', ')}`}
                  style={{
                    background: 'linear-gradient(135deg, #1565C0, #7B1FA2)',
                    color: '#fff',
                    borderRadius: '20px',
                    padding: '4px 10px',
                    fontSize: '10px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 6px rgba(21,101,192,0.3)',
                    cursor: 'help',
                  }}>
                  📊 {PRODUCT_OPTIONS.length} Products · {SECTOR_ORDER.length} Sectors
                </span>
              </label>
              {subscriberMode?.locked ? (
                <div style={{ padding: '10px 14px', background: '#f5f5f5', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', color: '#666' }}>
                  🔒 {subscriberMode.product_name} <span style={{ fontSize: '11px', color: '#999' }}>({subscriberMode.product_type})</span>
                </div>
              ) : (
              <>
              {/* Searchable product type selector — mirrors signup page exactly */}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="🔍 Search by sector, industry, or product name..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  onFocus={() => setProductSearch(productSearch || '')}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' }}
                />
                {/* Selected product display */}
                {form.product_type && !productSearch && (
                  <div style={{ fontSize: '12px', color: '#1565C0', marginTop: '4px', fontWeight: 500 }}>
                    ✅ Selected: {PRODUCT_OPTIONS.find(p => p.value === form.product_type)?.emoji} {PRODUCT_OPTIONS.find(p => p.value === form.product_type)?.label}
                  </div>
                )}
                {/* Scrollable dropdown list */}
                <div style={{
                  maxHeight: '280px',
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: '0 0 8px 8px',
                  marginTop: '2px',
                  background: '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}>
                  {SECTOR_ORDER.map(sector => {
                    const sectorProducts = filteredProducts.filter(p => p.sector === sector);
                    const sorted = [...sectorProducts].sort((a, b) => {
                      const tierA = a.tier || 1;
                      const tierB = b.tier || 1;
                      if (tierA !== tierB) return tierA - tierB;
                      return a.label.localeCompare(b.label);
                    });
                    return (
                      <div key={sector}>
                        {/* Sector Header */}
                        <div style={{
                          padding: '8px 14px',
                          background: '#1a1a2e',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '12px',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                        }}>
                          📊 {SECTOR_ORDER.indexOf(sector) + 1}. {sector} {sorted.length > 0 ? `(${sorted.length})` : ''}
                        </div>
                        {/* Products in this sector */}
                        {sorted.length > 0 ? (() => {
                          const tiers = [...new Set(sorted.map(p => p.tier || 1))].sort();
                          return tiers.map(tier => {
                            const tierProducts = sorted.filter(p => (p.tier || 1) === tier);
                            if (tierProducts.length === 0) return null;
                            return (
                              <div key={tier}>
                                {/* Tier sub-header */}
                                <div title={tier === 1 ? "CEO Smart Dashboard — Enterprise-wide KPIs, AI insights, and strategic directives at the C-suite level. Single executive view across all business units. LOB = Line of Business." : tier === 2 ? "Business Unit Smart Dashboard — CEO view + one Line of Business (LOB) drilldown. Combines executive-level intelligence with department-specific metrics, workflows, and AI recommendations for a single business unit." : "Operational Smart Dashboard — CEO view + one LOB + one Sub-Line of Business (Sub.LOB) drilldown. Full operational depth: executive strategy → business unit performance → granular team/process-level intelligence and automation."} style={{
                                  padding: '5px 14px 5px 16px',
                                  background: tier === 1 ? '#E8F5E9' : tier === 2 ? '#F3E5F5' : '#E3F2FD',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  color: tier === 1 ? '#2E7D32' : tier === 2 ? '#6A1B9A' : '#1565C0',
                                  borderLeft: `3px solid ${tier === 1 ? '#4CAF50' : tier === 2 ? '#7B1FA2' : '#1976D2'}`,
                                  cursor: 'help',
                                }}>
                                  {tier === 1 ? "● Type-1 — CEO's Smart Dashboard" : tier === 2 ? '● Type-2 — (CEO + 1 LOB) Smart Dashboard' : '● Type-3 — (CEO + 1 LOB w 1 Sub.LOB) Smart Dashboard'}
                                </div>
                                {tierProducts.map(p => (
                                  <div
                                    key={p.value}
                                    onClick={() => { if (p.dbReady) { setForm({ ...form, product_type: p.value, product_name: PRODUCT_NAMES[p.value] || p.label }); setProductSearch(''); } }}
                                    style={{
                                      padding: '8px 14px 8px 28px',
                                      cursor: p.dbReady ? 'pointer' : 'not-allowed',
                                      background: form.product_type === p.value ? '#E3F2FD' : '#fff',
                                      borderLeft: form.product_type === p.value ? '3px solid #1565C0' : '3px solid transparent',
                                      opacity: p.dbReady ? 1 : 0.45,
                                      fontSize: '13px',
                                      color: p.dbReady ? '#333' : '#999',
                                      transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={e => { if (p.dbReady) e.currentTarget.style.background = form.product_type === p.value ? '#E3F2FD' : '#f5f5f5'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = form.product_type === p.value ? '#E3F2FD' : '#fff'; }}
                                  >
                                    {p.emoji} {p.label} — <span style={{ color: '#888', fontSize: '11px' }}>{p.subSector}</span>
                                    {p.dbReady && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#4CAF50' }}>✅</span>}
                                    {!p.dbReady && <span style={{ marginLeft: '6px', fontSize: '10px' }}>🔜</span>}
                                    {p.tier === 2 && <span style={{ marginLeft: '6px', fontSize: '10px', background: '#7B1FA2', color: '#fff', padding: '1px 5px', borderRadius: '3px' }}>T2</span>}
                                    {p.isSubscription && <span style={{ marginLeft: '6px', fontSize: '10px', background: '#0D47A1', color: '#fff', padding: '1px 5px', borderRadius: '3px' }}>SUB</span>}
                                  </div>
                                ))}
                              </div>
                            );
                          });
                        })() : (
                          <div style={{ padding: '8px 14px 8px 24px', fontSize: '12px', color: '#bbb', fontStyle: 'italic' }}>
                            🔜 Coming Soon — No products available yet in this sector
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                ✅ = Live Database &nbsp;|&nbsp; 🔜 = Coming Soon &nbsp;|&nbsp; T2 = Enterprise ($1,000/mo) &nbsp;|&nbsp; SUB = Subscription
              </div>
              {productSearch && (
                <div style={{ fontSize: '11px', color: '#1565C0', marginTop: '2px' }}>
                  Showing {filteredProducts.length} of {PRODUCT_OPTIONS.length} products matching "{productSearch}"
                </div>
              )}
              </>
              )}
            </div>
            <div className="field full-width">
              <label htmlFor="product_name">Product / Application Name *</label>
              <input id="product_name" name="product_name" value={form.product_name} onChange={handleFormChange} placeholder="e.g. Restaurant Intelligence, Patient Portal, Fleet Tracker" required disabled={subscriberMode?.locked} style={subscriberMode?.locked ? { background: '#f5f5f5', color: '#666' } : {}} />
            </div>
          </div>

          <h2>Platform Role</h2>
          {subscriberMode?.locked ? (
            <div style={{ padding: '12px 16px', background: '#F3E5F5', borderRadius: '8px', border: '1px solid #CE93D8', fontSize: '13px', color: '#4A148C', marginBottom: '16px' }}>
              <span style={{ fontWeight: 700 }}>🔒 Subscriber</span> — Locked for your signup. Subscribe to builder-developed products and customize them for your own business.
            </div>
          ) : (
          <div className="tenant-type-grid">
            <div className={`tenant-card ${form.tenant_role === 'builder' ? 'selected' : ''}`} onClick={() => setForm({ ...form, tenant_role: 'builder' })} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setForm({ ...form, tenant_role: 'builder' })} aria-pressed={form.tenant_role === 'builder'}>
              <span className="tenant-icon">🔧</span>
              <h3>Product Builder</h3>
              <p>Create new product types, attach intelligence, and distribute to subscribers on a subscription basis.</p>
            </div>
            <div className={`tenant-card ${form.tenant_role === 'subscriber' ? 'selected' : ''}`} onClick={() => setForm({ ...form, tenant_role: 'subscriber' })} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setForm({ ...form, tenant_role: 'subscriber' })} aria-pressed={form.tenant_role === 'subscriber'}>
              <span className="tenant-icon">📊</span>
              <h3>Subscriber</h3>
              <p>Subscribe to builder-developed products and customize them for your own business with read-only DB access.</p>
            </div>
            <div className={`tenant-card ${form.tenant_role === 'distributor' ? 'selected' : ''}`} onClick={() => setForm({ ...form, tenant_role: 'distributor' })} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setForm({ ...form, tenant_role: 'distributor' })} aria-pressed={form.tenant_role === 'distributor'}>
              <span className="tenant-icon">🚚</span>
              <h3>Distributor</h3>
              <p>Resell and distribute builder products to your network of businesses.</p>
            </div>
          </div>
          )}

          <h2>Client Type</h2>
          {subscriberMode?.locked ? (
            <div style={{ padding: '12px 16px', background: '#E3F2FD', borderRadius: '8px', border: '1px solid #90CAF9', fontSize: '13px', color: '#0D47A1', marginBottom: '16px' }}>
              <span style={{ fontWeight: 700 }}>🔒 Standalone</span> — Single independent business. Locked for your signup.
            </div>
          ) : (
          <div className="tenant-type-grid">
            <div className={`tenant-card ${form.tenant_type === 'standalone' ? 'selected' : ''}`} onClick={() => setForm({ ...form, tenant_type: 'standalone', parent_tenant_id: '' })} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setForm({ ...form, tenant_type: 'standalone', parent_tenant_id: '' })} aria-pressed={form.tenant_type === 'standalone'}>
              <span className="tenant-icon">🏢</span>
              <h3>Standalone</h3>
              <p>{UI.STANDALONE_DESC}</p>
            </div>
            <div className={`tenant-card ${form.tenant_type === 'parent' ? 'selected' : ''}`} onClick={() => setForm({ ...form, tenant_type: 'parent', parent_tenant_id: '' })} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setForm({ ...form, tenant_type: 'parent', parent_tenant_id: '' })} aria-pressed={form.tenant_type === 'parent'}>
              <span className="tenant-icon">🏗️</span>
              <h3>Chain / Franchise Owner</h3>
              <p>{UI.CHAIN_OWNER_DESC}</p>
            </div>
            <div className={`tenant-card ${form.tenant_type === 'sub_client' ? 'selected' : ''}`} onClick={() => setForm({ ...form, tenant_type: 'sub_client' })} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setForm({ ...form, tenant_type: 'sub_client' })} aria-pressed={form.tenant_type === 'sub_client'}>
              <span className="tenant-icon">📍</span>
              <h3>Sub-Client (Location)</h3>
              <p>{UI.SUB_CLIENT_DESC}</p>
            </div>
            {/* ── Mall tenant type (Task 14.1) ── */}
            <div
              className={`tenant-card ${form.tenant_type === 'mall' ? 'selected' : ''}`}
              onClick={() => setForm({ ...form, tenant_type: 'mall', parent_tenant_id: '' })}
              role="button" tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setForm({ ...form, tenant_type: 'mall', parent_tenant_id: '' })}
              aria-pressed={form.tenant_type === 'mall'}
              style={{ border: form.tenant_type === 'mall' ? '2px solid #4f46e5' : undefined }}
            >
              <span className="tenant-icon">🏬</span>
              <h3>Shopping Mall</h3>
              <p>Register a physical shopping mall as a PARENT tenant. Onboard multiple businesses as sub-tenants with cross-tenant A2A and MCP intelligence.</p>
            </div>
          </div>
          )}

          {/* Mall Location Selector — shown when Mall tenant type is selected (Task 14.1) */}
          {form.tenant_type === 'mall' && (
            <div style={{ marginTop: '16px' }}>
              <LocationSelector
                value={mallLocation}
                onChange={(loc) => { setMallLocation(loc); setMallLocationTouched(true); }}
                required={true}
                showValidation={mallLocationTouched}
              />
              {mallLocationTouched && !isLocationValid(mallLocation) && (
                <div style={{
                  padding: '8px 12px', background: '#fef2f2',
                  border: '1px solid #fca5a5', borderRadius: '6px',
                  fontSize: '12px', color: '#dc2626', marginTop: '4px',
                }}>
                  ⚠ All four location fields are required for mall registration.
                </div>
              )}
            </div>
          )}

          {form.tenant_type === 'sub_client' && (
            <div className="parent-selector">
              <label htmlFor="parent_tenant_id">Parent Chain Client ID *</label>
              <input id="parent_tenant_id" name="parent_tenant_id" value={form.parent_tenant_id} onChange={handleFormChange} placeholder="e.g. client_a1b2c3" required />
              <small>Enter the Client ID of the chain/franchise owner this location belongs to</small>
            </div>
          )}

          {/* Application Model — only show for Chain/Franchise, auto-set for Standalone */}
          {form.tenant_type === 'PARENT' ? (
            <>
              <h2>Application Model</h2>
              <div className="app-model-grid">
                <div className={`app-model-card ${form.franchise_or_retail_subscription === 'franchise' ? 'selected' : ''}`} onClick={() => setForm({ ...form, franchise_or_retail_subscription: 'franchise' })} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setForm({ ...form, franchise_or_retail_subscription: 'franchise' })} aria-pressed={form.franchise_or_retail_subscription === 'franchise'}>
                  <span className="app-model-icon">🏪</span>
                  <h3>Franchise</h3>
                  <p>{UI.FRANCHISE_DESC}</p>
                </div>
                <div className={`app-model-card ${form.franchise_or_retail_subscription === 'retail_subscription' ? 'selected' : ''}`} onClick={() => setForm({ ...form, franchise_or_retail_subscription: 'retail_subscription' })} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setForm({ ...form, franchise_or_retail_subscription: 'retail_subscription' })} aria-pressed={form.franchise_or_retail_subscription === 'retail_subscription'}>
                  <span className="app-model-icon">🛒</span>
                  <h3>Retail Subscription</h3>
                  <p>{UI.RETAIL_SUB_DESC}</p>
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: '8px 12px', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #bbdefb', fontSize: '12px', color: '#1565C0', marginBottom: '8px' }}>
              📋 Application Model: <span style={{ fontWeight: 700 }}>{form.product_type === 'China-US-Strategic-Intel.Type-1' ? 'EAIS Subscription Intelligence' : 'Retail Subscription'}</span> (auto-selected for {form.product_type === 'China-US-Strategic-Intel.Type-1' ? 'GeoPulse' : 'Standalone'})
            </div>
          )}

          {form.product_type === 'China-US-Strategic-Intel.Type-1' && (
            <div style={{ padding: '12px 16px', background: '#E3F2FD', borderRadius: '8px', border: '1px solid #90CAF9', fontSize: '12px', color: '#0D47A1', marginBottom: '16px' }}>
              <div style={{ fontWeight: 700, marginBottom: '8px' }}>🌏 GeoPulse: China-US Strategic Intelligence</div>
              <div style={{ marginBottom: '6px' }}>🔒 <strong>Platform Role:</strong> Subscriber &nbsp;|&nbsp; <strong>Client Type:</strong> Subscription &nbsp;|&nbsp; <strong>Database:</strong> PostgreSQL (china-us-intel-v1)</div>
              <div style={{ marginBottom: '6px', fontSize: '11px', color: '#1565C0' }}>
                Transforms China-US geopolitical events into personalized commercial meaning for YOUR company, portfolio, supply chain, and career.
                Powered by CID-DDLS (CEO&apos;s Intelligence Directives — Dynamic Data Language System).
              </div>
              <div style={{ fontSize: '11px', color: '#333' }}>
                <strong>Live Data:</strong> 25 events including Trump-Xi Summit (May 14-15, 2026) &nbsp;|&nbsp; <strong>Forecasts:</strong> Bull 70% / Base 20% / Bear 10% (Nov 2026 tariff deadline)
              </div>
            </div>
          )}

          <h2>Select Plan</h2>
          {subscriberMode?.locked && form.product_type !== 'China-US-Strategic-Intel.Type-1' ? (() => {
            // Pull pricing from PRODUCT_DB_MAP for this product type
            const ptKey = Object.keys(PRODUCT_DB_MAP).find(k =>
              PRODUCT_DB_MAP[k]?.displayName === form.product_type || k === form.product_type
            );
            const ptConfig = ptKey ? PRODUCT_DB_MAP[ptKey] : null;
            const monthlyPrice = ptConfig?.pricingMonthly || 49;
            const planLabel = monthlyPrice >= 100 ? 'Enterprise' : 'Growth';
            return (
            <div style={{ padding: '12px 16px', background: '#E8F5E9', borderRadius: '8px', border: '1px solid #A5D6A7', fontSize: '13px', color: '#1B5E20', marginBottom: '16px' }}>
              <span style={{ fontWeight: 700 }}>🔒 {planLabel} Plan — ${monthlyPrice}/month</span>
              <div style={{ marginTop: '6px', fontSize: '12px' }}>45-day free trial → then ${monthlyPrice}/month. Locked for your signup.</div>
              <ul style={{ margin: '8px 0 0', paddingLeft: '20px', fontSize: '12px' }}>
                <li>Full BI + Top-10 questions</li>
                <li>{monthlyPrice >= 100 ? 'Unlimited' : '20'} LLM queries/day</li>
                <li>{monthlyPrice >= 100 ? '10' : '3'} RAG documents</li>
                <li>PDF export</li>
                <li>{monthlyPrice >= 100 ? '25' : '5'} users</li>
              </ul>
            </div>
            );
          })() : (
          <div className="plan-grid">
            {(form.product_type === 'China-US-Strategic-Intel.Type-1' ? PLANS.geopolitical_subscription : form.tenant_role === 'subscriber' ? PLANS.retail_subscriber : PLANS.default).map((p) => (
              <div key={p.id} className={`plan-card ${form.plan === p.id ? 'selected' : ''} ${p.recommended ? 'recommended' : ''}`} onClick={() => setForm({ ...form, plan: p.id })} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setForm({ ...form, plan: p.id })} aria-pressed={form.plan === p.id} title={p.tooltip || ''}>
                {p.tag && <div style={{ fontSize: '10px', color: '#1565C0', fontWeight: 600, marginBottom: '4px' }}>{p.tag}</div>}
                <h3>{p.name}</h3>
                <div className="plan-price">
                  {p.originalPrice && <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '14px', marginRight: '6px' }}>{p.originalPrice}</span>}
                  {p.price}
                </div>
                {p.trial_days && <div style={{ fontSize: '10px', color: '#4A148C', fontWeight: 600, marginTop: '2px' }}>{p.trial_days}-day free trial</div>}
                {p.discount && <div style={{ fontSize: '10px', color: '#2E7D32', fontWeight: 700, background: '#E8F5E9', padding: '2px 8px', borderRadius: '10px', display: 'inline-block', marginTop: '4px' }}>🎉 {p.discount}</div>}
                <ul>{p.features.map((f) => <li key={f}>✓ {f}</li>)}</ul>
              </div>
            ))}
          </div>
          )}

          {/* BAI Goal + Gov/Mil FREE coupon notice */}
          <div style={{ marginTop: '16px', padding: '14px 18px', background: 'linear-gradient(135deg, #E3F2FD 0%, #F3E5F5 100%)', borderRadius: '10px', border: '1px solid #BBDEFB' }}>
            <div style={{ fontWeight: 700, color: '#1565C0', fontSize: '14px', marginBottom: '6px' }}>🎯 Goal of Business Atomic Intelligence (BAI):</div>
            <div style={{ fontSize: '12.5px', color: '#333', lineHeight: '1.6', marginBottom: '10px' }}>
              Deliver real-time, AI-powered business intelligence tailored to your specific product type — from CEO-level dashboards to supply chain analytics — using RAG, GenAI, and Agentic AI layers working together.
            </div>
            <div style={{ fontSize: '12px', color: '#1B5E20', background: '#E8F5E9', padding: '8px 12px', borderRadius: '6px', borderLeft: '3px solid #43A047' }}>
              <strong>🏛️ Military & Government Professionals:</strong>{' '}
              Users with <code>.mil</code> or <code>.gov</code> email domains receive a <strong>FREE 1-month coupon</strong> anytime for new business registration. No payment required during trial.
            </div>
          </div>

          <div className="actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (editMode ? 'Saving...' : 'Registering...') : (editMode ? 'Save & Next →' : 'Register Client →')}
            </button>
          </div>
        </form>
      )}

      {/* Step 2: Database Config (MOVED from old Step 3) */}
      {step === 2 && (
        <form className="card" onSubmit={handleDbSubmit}>
          <h2>🗄️ Database Setup</h2>
          <p className="hint">Provide read-only database credentials for LLM operational queries. The system will perform a Schema Crawl to extract table definitions for intelligent Q&A. You can skip this and configure later.</p>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="engine">Engine</label>
              <select id="engine" name="engine" value={dbConfig.engine} onChange={handleDbChange}>
                {supportedEngines.length > 0 ? (
                  supportedEngines.map(eng => (
                    <option key={eng.engine} value={eng.engine}>{eng.display_name}</option>
                  ))
                ) : (
                  <>
                    <option value="postgresql">PostgreSQL</option>
                    <option value="mysql">MySQL</option>
                    <option value="mssql">SQL Server</option>
                    <option value="sqlite">SQLite</option>
                    <option value="mariadb">MariaDB</option>
                  </>
                )}
              </select>
            </div>

            {/* ── Factory Default: Use PNP Demo Database (product_type aware) ── */}
            {(() => {
              // Use subscriberMode product_type as the canonical source for demo db lookup
              // (form.product_type may be a slug or interim value during render)
              const productTypeForDemo = subscriberMode?.product_type || form.product_type;
              const demoDb = getDemoDbForProductType(productTypeForDemo);
              // isUsingDemo: host is any known demo alias AND database matches the product's demo DB
              const isUsingDemo =
                DEMO_HOSTS.includes(dbConfig.host) &&
                dbConfig.database === demoDb.database;
              return (
                <div className={`rls-toggle-card ${isUsingDemo ? 'active' : ''}`} style={{
                  marginBottom: '16px',
                  border: isUsingDemo ? '2px solid #1565C0' : '2px dashed #90CAF9',
                  background: isUsingDemo ? '#E3F2FD' : '#F8FBFF',
                  borderRadius: '10px',
                  padding: '12px 16px',
                }}>
                  {isUsingDemo && (
                    <div style={{
                      display: 'inline-block', marginBottom: '8px',
                      background: '#1565C0', color: '#fff',
                      fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px',
                      padding: '2px 10px', borderRadius: '10px',
                    }}>
                      ✅ FACTORY DEFAULT — ACTIVE
                    </div>
                  )}
                  <label className="rls-toggle-label" htmlFor="use_pnp_demo" style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      id="use_pnp_demo"
                      checked={isUsingDemo}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setDbConfig(prev => ({ ...prev, ...demoDb }));
                        } else {
                          setDbConfig(prev => ({ ...prev, host: '', port: '5432', database: '', username: '', password: '' }));
                        }
                      }}
                    />
                    <span className="rls-toggle-icon">🏭</span>
                    <span className="rls-toggle-text">
                      <span className="rls-toggle-title" style={{ fontSize: '14px', fontWeight: 700, color: '#0D47A1' }}>
                        Use PNP Demo Database (Factory Default)
                      </span>
                      <span className="rls-toggle-desc" style={{ fontSize: '12px', color: '#1565C0' }}>
                        {isUsingDemo
                          ? <>✅ Connected to PNP demo database: <code style={{ background: '#BBDEFB', padding: '1px 5px', borderRadius: '3px' }}>{demoDb.database}</code> — your AI is ready to query immediately. Connect your own database later via <strong>Update Client</strong>.</>
                          : <>Auto-fills with the PNP demo database for <strong>{form.product_type || 'your product type'}</strong>{demoDb.database ? <> → <code>{demoDb.database}</code></> : ''}. Connect your own database later via Update Client.</>
                        }
                      </span>
                    </span>
                  </label>
                </div>
              );
            })()}

            <div className="field">
              <label htmlFor="db_host">Host</label>
              <input id="db_host" name="host" value={dbConfig.host} onChange={handleDbChange} placeholder="db.internal.vpc" />
            </div>
            <div className="field">
              <label htmlFor="db_port">Port</label>
              <input id="db_port" name="port" value={dbConfig.port} onChange={handleDbChange} placeholder="5432" />
            </div>
            <div className="field">
              <label htmlFor="db_database">Database Name</label>
              <input id="db_database" name="database" value={dbConfig.database} onChange={handleDbChange} placeholder="company_ops" />
            </div>
            <div className="field">
              <label htmlFor="db_username">Username (read-only)</label>
              <input id="db_username" name="username" value={dbConfig.username} onChange={handleDbChange} placeholder="pnp_readonly" />
            </div>
            <div className="field">
              <label htmlFor="db_password">Password</label>
              <input id="db_password" name="password" type="password" value={dbConfig.password} onChange={handleDbChange} placeholder="••••••••" />
            </div>
          </div>

          <h2>🔒 Tenant Isolation (Row-Level Security)</h2>
          <p className="hint">Configure data visibility boundaries. These settings control which SQL Views the LLM layer uses to enforce the "need to know" principle at the database level.</p>

          <div className="rls-toggle-grid">
            <div className={`rls-toggle-card ${dbConfig.is_sub_client ? 'active' : ''}`}>
              <label className="rls-toggle-label" htmlFor="is_sub_client">
                <input type="checkbox" id="is_sub_client" name="is_sub_client" checked={dbConfig.is_sub_client} onChange={(e) => setDbConfig({ ...dbConfig, is_sub_client: e.target.checked, parent_chain_id: e.target.checked ? dbConfig.parent_chain_id : '' })} />
                <span className="rls-toggle-icon">📍</span>
                <span className="rls-toggle-text">
                  <span className="rls-toggle-title">Is this a Sub-Client?</span>
                  <span className="rls-toggle-desc">If yes, this location's data is isolated via <code>view_local_manager_data</code>.</span>
                </span>
              </label>
              {dbConfig.is_sub_client && (
                <div className="rls-sub-field">
                  <label htmlFor="parent_chain_id">Parent / Chain Owner Client ID</label>
                  <input id="parent_chain_id" name="parent_chain_id" value={dbConfig.parent_chain_id} onChange={handleDbChange} placeholder="e.g. client_a1b2c3" />
                  <small>The franchise owner who can see this location's data in their chain-wide view</small>
                </div>
              )}
            </div>

            <div className={`rls-toggle-card ${dbConfig.allow_distributor_insights ? 'active' : ''}`}>
              <label className="rls-toggle-label" htmlFor="allow_distributor_insights">
                <input type="checkbox" id="allow_distributor_insights" name="allow_distributor_insights" checked={dbConfig.allow_distributor_insights} onChange={(e) => setDbConfig({ ...dbConfig, allow_distributor_insights: e.target.checked, distributor_agent_ids: e.target.checked ? dbConfig.distributor_agent_ids : '' })} />
                <span className="rls-toggle-icon">🚚</span>
                <span className="rls-toggle-text">
                  <span className="rls-toggle-title">Allow Distributor Insights?</span>
                  <span className="rls-toggle-desc">If yes, approved distributors can request temporary read-only access via A2A/MCP.</span>
                </span>
              </label>
              {dbConfig.allow_distributor_insights && (
                <div className="rls-sub-field">
                  <label htmlFor="distributor_agent_ids">Authorized Comm-AI IDs</label>
                  <input id="distributor_agent_ids" name="distributor_agent_ids" value={dbConfig.distributor_agent_ids} onChange={handleDbChange} placeholder="e.g. pnp-agent-dist001, pnp-agent-dist002" />
                  <small>Comma-separated list of distributor agent IDs</small>
                </div>
              )}
            </div>
          </div>

          <h2>📊 BI Question Sharing</h2>
          <p className="hint">Control whether your business intelligence questions can be shared with other organizations on the platform. Sharing is limited to question text only — SQL, NLP instructions, and solutions are never shared.</p>

          <div className={`rls-toggle-card ${biSharingConsent ? 'active' : ''}`}>
            <label className="rls-toggle-label" htmlFor="bi_sharing_consent" style={{ cursor: 'pointer' }}>
              <input type="checkbox" id="bi_sharing_consent" checked={biSharingConsent} onChange={(e) => setBiSharingConsent(e.target.checked)} />
              <span className="rls-toggle-icon">🌐</span>
              <span className="rls-toggle-text">
                <span className="rls-toggle-title">Allow sharing of BI questions with other organizations?</span>
                <span className="rls-toggle-desc">If enabled, your business questions (not solutions) can be shared with specified clients. Default: <strong>NO</strong>.</span>
              </span>
            </label>
            {biSharingConsent && (
              <div className="rls-sub-field">
                <label htmlFor="bi_sharing_client_ids">Approved Client IDs for Sharing</label>
                <input id="bi_sharing_client_ids" value={biSharingClientIds} onChange={(e) => setBiSharingClientIds(e.target.value)} placeholder="e.g. client_a1b2c3, client_x9y8z7 (comma-separated)" />
                <small>Only these client IDs will see your shared BI questions. Leave blank to share with all clients of the same business type.</small>
              </div>
            )}
          </div>

          {/* ── DB Connection Test Indicator ── */}
          {dbTestStatus === 'testing' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#E3F2FD', border: '1px solid #90CAF9', borderRadius: '8px', marginBottom: '12px', fontSize: '13px', color: '#1565C0' }}>
              <span style={{ fontSize: '18px', animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
              <span>Testing database connection...</span>
            </div>
          )}
          {dbTestStatus === 'ok' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: '8px', marginBottom: '12px', fontSize: '13px', color: '#1B5E20' }}>
              <span style={{ fontSize: '18px' }}>✅</span>
              <span><strong>Connected:</strong> {dbTestMessage}</span>
            </div>
          )}
          {dbTestStatus === 'error' && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 16px', background: '#FFEBEE', border: '1px solid #EF9A9A', borderRadius: '8px', marginBottom: '12px', fontSize: '13px', color: '#B71C1C' }}>
              <span style={{ fontSize: '18px', flexShrink: 0 }}>❌</span>
              <div>
                <strong>Connection failed.</strong> Please fix the details above and try again.
                <div style={{ marginTop: '4px', fontFamily: 'monospace', fontSize: '12px', color: '#C62828', wordBreak: 'break-word' }}>{dbTestMessage}</div>
              </div>
            </div>
          )}

          <div className="actions">
            <button type="button" className="btn-secondary" onClick={() => setStep(1)}>← Back</button>
            <button type="button" className="btn-link" onClick={skipDb}>{editMode ? 'Skip →' : 'Skip for now'}</button>
            <button type="submit" className="btn-primary" disabled={loading || dbTestStatus === 'testing'}>
              {editMode && !dbModified ? 'Next →' : dbTestStatus === 'testing' ? 'Testing Connection...' : loading ? 'Saving & Crawling Schema...' : 'Save & Extract Schema →'}
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Data Sovereignty Consent + Schema Intelligence (Hard Stop) */}
      {step === 3 && (
        <div className="card">
          {crawling ? (
            <div className="center-card" style={{ padding: '40px 0' }}>
              <div className="setup-icon" style={{ fontSize: '48px' }}>🔍</div>
              <p>Crawling database schema...</p>
            </div>
          ) : (
            <>
              <SovereigntyConsent
                schemaCrawlResult={schemaCrawlResult}
                onConsentGranted={handleSovereigntyGranted}
                onConsentDenied={handleSovereigntyDenied}
                clientName={form.company_name}
                loading={loading}
              />
              <div className="actions" style={{ marginTop: '12px' }}>
                <button type="button" className="btn-secondary" onClick={() => setStep(2)}>← Back to Database</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 4: GenAI Preview & Approval (pnp_admin read-only review) */}
      {step === 4 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h2 style={{ margin: 0 }}>📋 GenAI Configuration Preview</h2>
              <p className="hint" style={{ margin: '4px 0 0' }}>
                Review the assembled GenAI config before activation. Access: <strong>pnp_admin (read-only)</strong>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* Download as PDF */}
              <button type="button" onClick={() => {
                if (!genaiPreview?.config_markdown) return;
                const win = window.open('', '_blank');
                win.document.write(`<!DOCTYPE html><html><head><title>GenAI Config — ${form.company_name}</title>
                  <style>body{font-family:Calibri,sans-serif;padding:24px;font-size:13px;color:#333;max-width:900px;margin:0 auto}
                  h1{color:#1a237e;font-size:20px}h2{color:#283593;font-size:16px;border-bottom:1px solid #ddd;padding-bottom:4px}
                  h3{color:#1565c0;font-size:14px}table{border-collapse:collapse;width:100%;margin:8px 0}
                  th,td{border:1px solid #ddd;padding:4px 8px;font-size:11px;text-align:left}th{background:#f5f5f5}
                  code{background:#f0f0f0;padding:1px 4px;border-radius:3px;font-size:11px}
                  pre{background:#f5f5f5;padding:8px;border-radius:4px;font-size:11px;overflow-x:auto}
                  .footer{margin-top:20px;border-top:1px solid #ddd;padding-top:8px;font-size:10px;color:#888}
                  @media print{body{padding:12px}}</style></head><body>`);
                // Simple markdown-to-HTML conversion
                const html = genaiPreview.config_markdown
                  .replace(/^### (.+)$/gm, '<h3>$1</h3>')
                  .replace(/^## (.+)$/gm, '<h2>$1</h2>')
                  .replace(/^# (.+)$/gm, '<h1>$1</h1>')
                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                  .replace(/`(.+?)`/g, '<code>$1</code>')
                  .replace(/^---$/gm, '<hr/>')
                  .replace(/^\| (.+)$/gm, (match) => {
                    const cells = match.split('|').filter(c => c.trim());
                    if (cells.every(c => /^[-\s:]+$/.test(c.trim()))) return '';
                    const tag = 'td';
                    return '<tr>' + cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('') + '</tr>';
                  })
                  .replace(/^- (.+)$/gm, '<li>$1</li>')
                  .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
                  .replace(/\n\n/g, '<br/><br/>')
                  .replace(/_(.+?)_/g, '<em>$1</em>');
                win.document.write(html);
                win.document.write(`<div class="footer">Generated by Plug-N-Play-Infra.ai • ${new Date().toLocaleString()}</div>`);
                win.document.write('</body></html>');
                win.document.close();
                setTimeout(() => win.print(), 300);
              }} style={{
                padding: '6px 14px', background: '#1565C0', color: '#fff', border: 'none',
                borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
              }}>
                📥 Download PDF
              </button>
            </div>
          </div>

          {genaiPreviewLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: '48px' }}>🔄</div>
              <p>Assembling GenAI configuration...</p>
              <p className="hint">Reading client database schema, injecting SQL intelligence, generating BI use cases...</p>
            </div>
          ) : genaiPreview ? (
            <>
              {/* Metadata summary */}
              <div className="result-box" style={{ marginBottom: '16px' }}>
                <div className="result-row"><span>Company</span><span>{genaiPreview.metadata?.company_name}</span></div>
                <div className="result-row"><span>Product Type</span><span>{genaiPreview.metadata?.product_type || '—'}</span></div>
                <div className="result-row"><span>Industry</span><span>{genaiPreview.metadata?.industry || '—'}</span></div>
                <div className="result-row"><span>Client DB Tables</span><span className="mono">{genaiPreview.metadata?.table_count || 0}</span></div>
                <div className="result-row"><span>DB Objects</span><span className="mono">{genaiPreview.metadata?.object_count || 0}</span></div>
                <div className="result-row"><span>Status</span><span className="status-badge" style={{ background: '#FF9800', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>PENDING APPROVAL</span></div>
              </div>

              {/* Full config preview (read-only, scrollable) */}
              <div style={{
                background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: '8px',
                padding: '16px', maxHeight: '400px', overflowY: 'auto', fontSize: '12px',
                lineHeight: 1.6, fontFamily: 'Consolas, monospace', whiteSpace: 'pre-wrap',
              }}>
                {genaiPreview.config_markdown}
              </div>

              {/* Approval notice */}
              <div style={{
                marginTop: '16px', padding: '12px 16px', background: '#fff3e0',
                border: '1px solid #ffcc80', borderRadius: '8px', fontSize: '13px',
              }}>
                ⚠️ By approving, the GenAI layer will be activated with this configuration.
                The client's actual database tables replace the default template.
                All SQL intelligence and BI use cases are injected for the selected product type.
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              No preview available. Click "Back" to configure the database first.
            </div>
          )}

          <div className="actions">
            <button type="button" className="btn-secondary" onClick={() => setStep(3)}>← Back</button>
            <button type="button" className="btn-primary" onClick={handleGenaiApprove} disabled={loading || !genaiPreview} style={{ background: '#2E7D32' }}>
              {loading ? 'Approving...' : '✅ Approve & Set GenAI'}
            </button>
          </div>
        </div>
      )}

      {/* Step 5: Upload 3 PDFs */}
      {step === 5 && (
        <div className="card">
          {/* ── Mall: Batch Onboarding option (Task 14.1) ── */}
          {form.tenant_type === 'mall' && (
            <div style={{
              background: '#f0f7ff', border: '1px solid #90caf9',
              borderRadius: '10px', padding: '16px', marginBottom: '20px',
            }}>
              <h3 style={{ margin: '0 0 8px', color: '#1565c0', fontSize: '15px' }}>
                🏪 Batch Onboard Sub-Tenants
              </h3>
              <p style={{ fontSize: '13px', color: '#555', margin: '0 0 12px' }}>
                Onboard all 11 Kona-Point businesses (5 existing + 6 new) as sub-tenants
                under this mall. You can do this now or skip and onboard individually later.
              </p>
              {!showBatchOnboarding && !batchResult && (
                <button
                  type="button"
                  style={{
                    padding: '8px 16px', background: '#1565c0', color: '#fff',
                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 600,
                  }}
                  onClick={() => setShowBatchOnboarding(true)}
                >
                  🚀 Start Batch Onboarding
                </button>
              )}
              {showBatchOnboarding && !batchResult && (
                <BatchOnboarding
                  mallTenantId={clientId}
                  onComplete={(result) => {
                    setBatchResult(result);
                    setShowBatchOnboarding(false);
                  }}
                />
              )}
              {batchResult && (
                <div style={{ fontSize: '13px', color: '#16a34a' }}>
                  ✅ Batch complete: {batchResult.succeeded} succeeded,{' '}
                  {batchResult.failed} failed out of {batchResult.total} total.
                </div>
              )}
            </div>
          )}

          {form.product_type && form.product_type !== 'CUSTOM' && form.product_type !== '' ? (
            <>
              <div className="setup-icon" style={{ fontSize: '48px', textAlign: 'center' }}>📋</div>
              <h2 style={{ textAlign: 'center' }}>Standard Template Selected</h2>
              <p className="hint" style={{ textAlign: 'center' }}>
                Product Type: <strong>{form.product_type}</strong><br/>
                Pre-validated {UI.RAG_AI}, {UI.GENAI}, and {UI.AGENTIC_AI} templates will be auto-applied from the Kona Global template registry. No document upload required.
              </p>
              <div className="actions">
                <button type="button" className="btn-secondary" onClick={() => setStep(4)}>← Back</button>
                <button type="button" className="btn-primary" onClick={() => setStep(6)}>Continue to Setup →</button>
              </div>
            </>
          ) : (
            <>
              <h2>Upload AI Configuration Documents</h2>
              <p className="hint">Upload the 3 documents that configure your client's AI layers. PDF or DOCX accepted.</p>

              <div className="upload-grid">
                {[
                  { key: 'rag_ai', label: UI.UPLOAD_RAG, desc: UI.UPLOAD_RAG_DESC, ref: ragRef, icon: UI.RAG_ICON },
                  { key: 'genai', label: UI.UPLOAD_GENAI, desc: UI.UPLOAD_GENAI_DESC, ref: genaiRef, icon: UI.GENAI_ICON },
                  { key: 'agentic_ai', label: UI.UPLOAD_AGENTIC, desc: UI.UPLOAD_AGENTIC_DESC, ref: agenticRef, icon: UI.AGENTIC_ICON },
                ].map(({ key, label, desc, ref, icon }) => (
                  <div key={key} className={`upload-card ${uploadProgress[key] === 'done' ? 'uploaded' : ''} ${uploadProgress[key] === 'error' ? 'upload-error' : ''}`}>
                    <div className="upload-icon">{icon}</div>
                    <h3>{label}</h3>
                    <p>{desc}</p>
                    <input type="file" accept=".pdf,.docx" ref={ref} onChange={(e) => handleFileSelect(key, e)} style={{ display: 'none' }} aria-label={`Upload ${label} document`} />
                    <button type="button" className="btn-secondary" onClick={() => ref.current.click()}>
                      {files[key] ? `✓ ${files[key].name}` : 'Choose File'}
                    </button>
                    {uploadProgress[key] === 'uploading' && <span className="upload-status">Uploading...</span>}
                    {uploadProgress[key] === 'done' && <span className="upload-status success">Uploaded</span>}
                    {uploadProgress[key] === 'error' && <span className="upload-status error">Failed</span>}
                  </div>
                ))}
              </div>

              <div className="actions">
                <button type="button" className="btn-secondary" onClick={() => setStep(4)}>← Back</button>
                <button type="button" className="btn-primary" onClick={handleUploadAll} disabled={loading || (!files.rag_ai && !files.genai && !files.agentic_ai)}>
                  {loading ? 'Uploading...' : 'Upload & Continue →'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 6: Payment/Coupon (subscriber mode) OR Trigger Setup (admin mode) */}
      {step === 6 && subscriberMode && !paymentValidated && (
        <div className="card center-card">
          <div className="setup-icon">💳</div>
          <h2>Activate Your Account</h2>
          <p style={{ color: '#555', marginBottom: '20px' }}>
            One final step — activate your account to trigger the AI setup for <strong>{form.company_name}</strong>.
          </p>

          {/* Billing summary — derived from selected plan */}
          {(() => {
            const planList = form.product_type === 'China-US-Strategic-Intel.Type-1'
              ? PLANS.geopolitical_subscription
              : form.tenant_role === 'subscriber' ? PLANS.retail_subscriber : PLANS.default;
            const selectedPlan = planList.find(p => p.id === form.plan) || planList[0];
            const trialDays = selectedPlan.trial_days || 45;
            const planPrice = selectedPlan.price || '$49/mo';
            const planName = selectedPlan.name || 'Growth';
            return (
              <div style={{ background: '#E8F5E9', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                <div style={{ fontWeight: 700, color: '#2E7D32', marginBottom: '8px' }}>📋 Billing Timeline</div>
                <table style={{ width: '100%', fontSize: '13px' }}>
                  <tbody>
                    <tr><td style={{ padding: '4px 0', color: '#333' }}>Today</td><td style={{ fontWeight: 600 }}>$5 account setup (one-time)</td></tr>
                    <tr><td style={{ padding: '4px 0', color: '#333' }}>Days 1–{trialDays}</td><td style={{ fontWeight: 600 }}>FREE — full access trial</td></tr>
                    <tr><td style={{ padding: '4px 0', color: '#333' }}>Day {trialDays + 1}+</td><td style={{ fontWeight: 600 }}>{planPrice} ({planName})</td></tr>
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Coupon input */}
          <div style={{ background: '#F3E5F5', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1.5px solid #CE93D8' }}>
            <div style={{ fontWeight: 700, color: '#6A1B9A', marginBottom: '6px', fontSize: '15px' }}>🎟️ Have a coupon code?</div>
            <div style={{ fontSize: '13px', color: '#555', marginBottom: '14px' }}>
              Enter a valid coupon to skip the $5 fee and start your free trial instantly.
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                placeholder="Enter coupon code (e.g. LAUNCH2026)"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponMessage(''); }}
                style={{ flex: 1, padding: '10px 14px', border: '1px solid #CE93D8', borderRadius: '8px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}
              />
              <button
                type="button"
                onClick={() => handleApplyCouponInWizard(clientId)}
                disabled={couponApplying}
                style={{ padding: '10px 20px', background: '#7B1FA2', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {couponApplying ? '...' : 'Apply'}
              </button>
            </div>
            {couponMessage && (
              <div style={{ fontSize: '13px', color: couponMessage.startsWith('✅') ? '#2E7D32' : '#C62828', marginBottom: '8px' }}>
                {couponMessage}
              </div>
            )}
          </div>

          {/* Promotional notice for government/public-sector users */}
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px', padding: '12px 16px', background: '#F5F5F5', borderRadius: '8px', borderLeft: '3px solid #1565C0' }}>
            <strong style={{ color: '#1565C0' }}>🏛️ Government & Public Sector:</strong>{' '}
            Users with official .gov or approved public-sector email domains may qualify for a 30-day free AI adoption trial for Type-1 CEO Dashboard products. Coupon codes such as LAUNCH2026 may be provided upon request and are subject to approval and expiration.
          </div>

          {paymentValidated && (
            <div style={{ padding: '14px 18px', background: '#E8F5E9', borderRadius: '10px', border: '1px solid #A5D6A7', fontSize: '14px', color: '#2E7D32', fontWeight: 600, marginBottom: '16px' }}>
              ✅ Account activated — your free trial has started!
            </div>
          )}

          <div className="actions">
            <button type="button" className="btn-secondary" onClick={() => setStep(5)}>← Back</button>
            {paymentValidated ? (
              <button type="button" className="btn-primary btn-large" onClick={handleTriggerSetup} disabled={loading}>
                {loading ? '🔄 Setting up...' : '🚀 Launch Setup →'}
              </button>
            ) : (
              <button type="button" className="btn-primary btn-large" onClick={async () => {
                // Redirect to Stripe $5 checkout
                const API = process.env.REACT_APP_API_BASE || '/v1';
                try {
                  const res = await fetch(`${API}/subscribers/create-checkout`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      organization_id: clientId || '',
                      email: form.contact_email || subscriberMode?.email || '',
                      product_type: form.product_type || subscriberMode?.product_type || '',
                      product_name: form.product_name || subscriberMode?.product_name || '',
                      company_name: form.company_name || subscriberMode?.company_name || '',
                      success_url: `${window.location.origin}/subscriber?payment=success`,
                      cancel_url: `${window.location.origin}/subscriber?payment=cancelled`,
                    }),
                  });
                  const data = await res.json();
                  if (data.checkout_url) {
                    try { localStorage.setItem('pnp_pending_onboard_config', JSON.stringify({ client_id: clientId, company_name: form.company_name, product_name: form.product_name, product_type: form.product_type, tenant_type: form.tenant_type, plan: form.plan })); } catch { /* ignore */ }
                    window.location.href = data.checkout_url;
                  }
                } catch { /* ignore */ }
              }}>
                💳 Pay $5 & Activate →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Step 6 (admin mode) / Step 7 (subscriber mode after payment): Trigger Setup */}
      {step === 6 && (!subscriberMode || paymentValidated) && (
        <div className="card center-card">
          <div className="setup-icon">⚡</div>
          <h2>Ready to Launch</h2>
          <p>This will trigger the AI pipeline setup for <strong>{form.company_name}</strong>:</p>
          <ul className="setup-list">
            <li>{UI.RAG_ICON} {UI.RAG_AI} — Parse & index knowledge base (~60s)</li>
            <li>{UI.GENAI_ICON} {UI.GENAI} — Extract system prompt & configure tools</li>
            <li>{UI.AGENTIC_ICON} {UI.AGENTIC_AI} — Store tokens & setup templates</li>
            {schemaConsent && <li>🧠 Schema Intelligence — Append DB schema to GenAI config</li>}
          </ul>
          <div className="actions">
            <button type="button" className="btn-secondary" onClick={() => setStep(5)}>← Back</button>
            <button type="button" className="btn-primary btn-large" onClick={handleTriggerSetup} disabled={loading}>
              {loading ? '🔄 Setting up...' : '🚀 Launch Setup'}
            </button>
          </div>
        </div>
      )}

      {/* Step 7: Setup Complete — proceed to Done */}
      {step === 7 && (
        <div className="card center-card">
          <div className="setup-icon">✅</div>
          <h2>Client Onboarded Successfully</h2>
          <div className="result-box">
            <div className="result-row"><span>Client ID</span><span className="mono">{clientId}</span></div>
            <div className="result-row"><span>Company</span><span>{form.company_name}</span></div>
            <div className="result-row"><span>Plan</span><span className="capitalize">{form.plan}</span></div>
            <div className="result-row"><span>Schema Intelligence</span><span className={`status-badge ${schemaConsent ? 'configured' : 'skipped'}`}>{schemaConsent ? '✅ Enabled (Sovereignty Verified)' : '⏭️ Skipped'}</span></div>
            {schemaConsent && <div className="result-row"><span>Data Perimeter</span><span className="status-badge configured">🛡️ In-and-Out Enforced</span></div>}
            {form.website_url && <div className="result-row"><span>Website</span><a href={form.website_url} target="_blank" rel="noopener noreferrer">{form.website_url}</a></div>}
            {form.contact_phone && <div className="result-row"><span>Phone</span><span>{form.contact_phone}</span></div>}
            {statusResult && (
              <>
                <div className="result-row"><span>{UI.RAG_STATUS}</span><span className={`status-badge ${statusResult.rag_status}`}>{statusResult.rag_status}</span></div>
                <div className="result-row"><span>{UI.GENAI_STATUS}</span><span className={`status-badge ${statusResult.genai_status}`}>{statusResult.genai_status}</span></div>
                <div className="result-row"><span>{UI.AGENTIC_STATUS}</span><span className={`status-badge ${statusResult.agentic_status}`}>{statusResult.agentic_status}</span></div>
              </>
            )}
            {dashboardUrl && (
              <div className="result-row"><span>Dashboard</span><a href={dashboardUrl} target="_blank" rel="noopener noreferrer">{dashboardUrl}</a></div>
            )}
            {/* ── Mall Dashboard URL (Task 14.1) ── */}
            {form.tenant_type === 'mall' && mallDashboardUrl && (
              <div className="result-row" style={{ background: '#f0f7ff', borderRadius: '6px', padding: '8px 12px' }}>
                <span style={{ fontWeight: 700, color: '#1565c0' }}>🏬 Mall Dashboard</span>
                <a href={mallDashboardUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', fontWeight: 600 }}>
                  {mallDashboardUrl}
                </a>
              </div>
            )}
            {form.tenant_type === 'mall' && batchResult && (
              <div className="result-row" style={{ background: '#f0fdf4', borderRadius: '6px', padding: '8px 12px' }}>
                <span style={{ fontWeight: 700, color: '#16a34a' }}>🏪 Sub-Tenants</span>
                <span style={{ color: '#16a34a' }}>
                  {batchResult.succeeded} onboarded successfully
                  {batchResult.failed > 0 && `, ${batchResult.failed} failed`}
                </span>
              </div>
            )}
          </div>

          {/* ── Payment / Coupon — subscriber mode only, shown before Next ── */}
          {subscriberMode && !paymentValidated && (
            <div style={{ margin: '20px 0', padding: '20px', background: '#F3E5F5', borderRadius: '12px', border: '1.5px solid #CE93D8' }}>
              <div style={{ fontWeight: 700, color: '#6A1B9A', marginBottom: '6px', fontSize: '15px' }}>🎟️ Activate Your Account</div>
              <div style={{ fontSize: '13px', color: '#555', marginBottom: '14px' }}>
                Enter a coupon code to skip the $5 fee and start your free trial instantly, or pay $5 to activate.
              </div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder="Enter coupon code (e.g. LAUNCH2026)"
                  value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponMessage(''); }}
                  style={{ flex: 1, padding: '10px 14px', border: '1px solid #CE93D8', borderRadius: '8px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}
                />
                <button
                  type="button"
                  onClick={() => handleApplyCouponInWizard(clientId)}
                  disabled={couponApplying}
                  style={{ padding: '10px 20px', background: '#7B1FA2', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {couponApplying ? '...' : 'Apply'}
                </button>
              </div>
              {couponMessage && (
                <div style={{ fontSize: '13px', color: couponMessage.startsWith('✅') ? '#2E7D32' : '#C62828', marginBottom: '8px' }}>
                  {couponMessage}
                </div>
              )}
              <div style={{ fontSize: '12px', color: '#9C27B0' }}>
                No coupon? <button type="button" onClick={async () => {
                  const API = process.env.REACT_APP_API_BASE || '/v1';
                  try {
                    const res = await fetch(`${API}/subscribers/create-checkout`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        organization_id: clientId || '',
                        signup_id: clientId || '',
                        email: form.contact_email || subscriberMode?.email || '',
                        product_type: form.product_type || subscriberMode?.product_type || '',
                        product_name: form.product_name || subscriberMode?.product_name || '',
                        company_name: form.company_name || subscriberMode?.company_name || '',
                        success_url: `${window.location.origin}/subscriber?payment=success`,
                        cancel_url: `${window.location.origin}/subscriber?payment=cancelled`,
                      }),
                    });
                    const data = await res.json();
                    if (data.checkout_url) {
                      try { localStorage.setItem('pnp_pending_onboard_config', JSON.stringify({ client_id: clientId, company_name: form.company_name, product_name: form.product_name, product_type: form.product_type, tenant_type: form.tenant_type, plan: form.plan })); } catch { /* ignore */ }
                      window.location.href = data.checkout_url;
                    } else if (data.coupon_applied) {
                      setPaymentValidated(true);
                      setCouponMessage('✅ Account activated!');
                    }
                  } catch { setCouponMessage('❌ Could not start checkout. Please try again.'); }
                }} style={{ background: 'none', border: 'none', color: '#1565C0', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', padding: 0 }}>Pay $5 to activate →</button>
              </div>
            </div>
          )}

          {subscriberMode && paymentValidated && (
            <div style={{ margin: '16px 0', padding: '14px 18px', background: '#E8F5E9', borderRadius: '10px', border: '1px solid #A5D6A7', fontSize: '14px', color: '#2E7D32', fontWeight: 600 }}>
              ✅ Account activated — your free trial has started!
            </div>
          )}

          <p className="hint">Setup typically completes within 5 minutes. The client can log into their dashboard once all statuses show "configured" or "indexed".</p>
          <div className="actions">
            {/* Next button — proceeds to Step 8 (Done) to finalize */}
            {(!subscriberMode || paymentValidated) && (
              <button type="button" className="btn-primary" onClick={() => {
                const finalConfig = {
                  client_id: paymentClientId || clientId,
                  company_name: form.company_name,
                  product_name: form.product_name,
                  product_type: form.product_type,
                  tenant_type: form.tenant_type,
                  tenant_role: form.tenant_role,
                  plan: form.plan,
                  location: form.location || '',
                  contact_email: form.contact_email || '',
                  parent_tenant_id: form.parent_tenant_id || null,
                  franchise_or_retail: form.franchise_or_retail_subscription,
                  website_url: form.website_url || null,
                  contact_phone: form.contact_phone || null,
                  schema_consent: schemaConsent,
                  bi_sharing_consent: biSharingConsent,
                  bi_sharing_client_ids: biSharingClientIds,
                };
                // Call onComplete to persist final state (DynamoDB write, session update)
                if (onComplete) {
                  onComplete(finalConfig);
                }
                setStep(8);
              }}>
                Next →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Step 8: Done — Final confirmation & Launch */}
      {step === 8 && (
        <div className="card center-card">
          <div className="setup-icon">🎉</div>
          <h2>All Set — You're Live!</h2>
          <p style={{ color: '#555', marginBottom: '20px' }}>
            Your business <strong>{form.company_name}</strong> is fully configured and ready to use.
          </p>
          <div className="result-box">
            <div className="result-row"><span>Client ID</span><span className="mono">{paymentClientId || clientId}</span></div>
            <div className="result-row"><span>Status</span><span className="status-badge configured">✅ Active</span></div>
            {dashboardUrl && (
              <div className="result-row"><span>Dashboard</span><a href={dashboardUrl} target="_blank" rel="noopener noreferrer">{dashboardUrl}</a></div>
            )}
          </div>
          <div className="actions" style={{ marginTop: '24px' }}>
            <button type="button" className="btn-primary" style={{ background: '#2E7D32' }} onClick={() => {
              const finalConfig = {
                client_id: paymentClientId || clientId,
                company_name: form.company_name,
                product_name: form.product_name,
                product_type: form.product_type,
                tenant_type: form.tenant_type,
                plan: form.plan,
                parent_tenant_id: form.parent_tenant_id || null,
                website_url: form.website_url || null,
                contact_phone: form.contact_phone || null,
              };
              if (onLaunchApp) {
                onLaunchApp(finalConfig);
              }
            }}>
              ▶ Launch Application
            </button>
            {!subscriberMode && (
              <button type="button" className="btn-primary" onClick={() => { setStep(1); setClientId(null); setUploadUrls(null); setFiles({ rag_ai: null, genai: null, agentic_ai: null }); setUploadProgress({ rag_ai: null, genai: null, agentic_ai: null }); setStatusResult(null); setSchemaCrawlResult(null); setSchemaConsent(false); setSovereigntyConsent({ consentA: false, consentB: false }); setDistillationResult(null); setGenaiPreview(null); setGenaiApproved(false); setError(''); }}>
                Onboard Another Client
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientOnboarding;
